import cn from 'classnames';
import { useMemo } from 'react';

import { useBodyTitleStyle } from '@webapp/common/components/custom-styles';
import { CssPageBlock } from '@webapp/common/components/survey-custom';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import type { AlignSetting } from '@webapp/common/lib/ui';
import { createTextStyle, mapQuestionBlockPositionToAlignItems, styleStrToObject } from '@webapp/common/lib/ui';

import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { SurveySection } from '../container';

import css from './survey-title.css';

export const SurveyTitle: FC<{
    inBody?: boolean;
    empty?: boolean;
    headerHeight?: number;
    align?: AlignSetting;
}> = ({ empty, headerHeight, inBody, align }) => {
    const { design, title } = useStateSelector(selectInfo);
    const isMobile = useMobile();

    const {
        brand: { titleStyle, mobileTitleStyle },
        fonts: { titleBold, titleColor, titleItalic, titleSize, titleThrough, titleUnderline },
    } = design;

    const titlePositionStyles = useMemo(
        () => styleStrToObject(isMobile && mobileTitleStyle ? mobileTitleStyle : titleStyle),
        [titleStyle, mobileTitleStyle, isMobile],
    );
    const textStyle = createTextStyle({
        underline: titleUnderline,
        through: titleThrough,
        bold: titleBold,
        italic: titleItalic,
        color: titleColor,
        size: titleSize,
    });

    const inHeadStyle: CSSProperties = useMemo(
        () => ({
            ...textStyle,
            ...(inBody ? {} : titlePositionStyles),
            justifyContent: mapQuestionBlockPositionToAlignItems[align],
            ['--mobile-height' as any]: `${headerHeight ? headerHeight / 2 : 0}px`,
        }),
        [headerHeight, inBody, textStyle, titlePositionStyles],
    );

    // @ts-expect-error
    const bodyTitleStyle = useBodyTitleStyle(design, textStyle);
    const bodyStyle = useMemo(
        () => ({
            ...bodyTitleStyle,
            ...textStyle,
            textAlign: align,
        }),
        [bodyTitleStyle, textStyle, align],
    );

    return inBody ? (
        <div
            className={cn(CssPageBlock.HEADER_TITLE, !inBody && css.title, css[align], {
                [css.titleMobile]: isMobile && mobileTitleStyle,
            })}
            style={bodyStyle}
        >
            {title}
        </div>
    ) : (
        <SurveySection className={cn(CssPageBlock.HEADER_TITLE_CONTAINER, css.container)}>
            {!empty && (
                <div
                    className={cn(CssPageBlock.HEADER_TITLE, css.title, css.inHead, css[align], {
                        [css.titleMobile]: isMobile && mobileTitleStyle,
                    })}
                    style={inHeadStyle}
                >
                    {title}
                </div>
            )}
        </SurveySection>
    );
};
SurveyTitle.displayName = 'SurveyTitle';
